import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { isAutoExchange } from 'src/utils/transactions-util';
import { moduleActions } from '../../../redux/modules';
import { getBucketRoll } from '../../../redux/modules/bucketRoll/bucketRoll.selectors';
import {
  getExchangeFromIsIRA,
  getExchangeHasOnlyRetirementFunds,
  getIsExchangeAmountWithinIRAContributionLimits,
  getSelectedExchangeFund,
} from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { getIsFullyContributedIRA } from '../../../redux/modules/funds/IRA/IRA.selectors';
import {
  getIRADataLookUp,
  getIsCorporateAccountType,
} from '../../../redux/modules/shoppingCart/shoppingCart.selectors';
import { PRODUCT_TYPE_LABEL_MAP, RETIREMENT } from '../../../settings/settings';
import { isProductType529 } from '../../../utils/funds/funds-utility';
import { trainCase } from '../../../utils/utils';
import ExpandedFundAgreementTypeHeader from '../../Buy/BuyFlow/ExpandedFundList/ExpandedFundAgreementTypeHeader';
import { ExpandedFundClassificationTypeHeader } from '../../Buy/BuyFlow/ExpandedFundList/ExpandedFundClassificationTypeHeader';
import IRAMaxAction from '../../Buy/BuyFlow/IRAMaxAction/IRAMaxAction';
import Note from '../../Shared/Note/Note';
import TRP from '../../Shared/TRP/TRP';
import ExchangeExcessContributionMessage from '../ExchangeExcessContributionMessage/ExchangeExcessContributionMessage';
import ExchangeIRAAvailable from '../ExchangeIRAAvailable/ExchangeIRAAvailable';
import './ExchangeToFundGroupHeader.scss';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import { getIsRMDEligible } from '../../../redux/modules/RMD/RMDSetting.selectors';

const ExchangeToFundGroupHeader = ({
  isSalesforceRep,
  customerWebViewEnabled,
  actions,
  bucketRoll,
  exchangeFromIsRetail,
  fund,
  hasOnlyRetirementFunds,
  IRADataLookUp,
  isCorporateAccountType,
  isExchangeWithinIRALimits,
  isFullyContributedIRA,
  isRMDEligible,
  lastFund,
  selectedFund,
}) => {
  const isNonFidPlan =
    selectedFund?.agreementType === 'NON_FIDUCIARY_RETIREMENT_PLAN';
  const isCollege = isProductType529(fund.productType);
  const transitionedProductType =
    (isCollege && !isProductType529(lastFund.productType)) ||
    (!isCollege && isProductType529(lastFund.productType));

  const firstOfClassification =
    transitionedProductType ||
    (isCollege
      ? lastFund.formattedEntityNames !== fund.formattedEntityNames
      : lastFund.displayClassification !== fund.displayClassification);

  const firstOfAgreement =
    transitionedProductType ||
    (isCollege
      ? lastFund.productType !== fund.productType ||
        lastFund.formattedEntityNames !== fund.formattedEntityNames
      : lastFund.agreementType !== fund.agreementType ||
        lastFund.formattedEntityNames !== fund.formattedEntityNames);
  const showRMDNote =
    firstOfAgreement &&
    isRMDEligible &&
    !fund.agreementType.endsWith('IRA') &&
    !exchangeFromIsRetail;

  const displayClassification = isCollege ? (
    <span className="blocked_data">{fund.formattedEntityNames}</span>
  ) : (
    trainCase(fund.displayClassification)
  );

  const agreementType = isCollege
    ? PRODUCT_TYPE_LABEL_MAP[fund.productType]
    : fund.agreementType;

  const showRetirementHeader =
    firstOfClassification &&
    fund.displayClassification === RETIREMENT &&
    !isCorporateAccountType &&
    exchangeFromIsRetail;

  const isCustomerViewDisabledAndIsSalesForceRep = () => {
    return !customerWebViewEnabled && isSalesforceRep;
  };

  return (
    <Fragment>
      {firstOfClassification && (
        <ExpandedFundClassificationTypeHeader>
          {displayClassification}
        </ExpandedFundClassificationTypeHeader>
      )}

      {showRetirementHeader && isFullyContributedIRA && (
        <IRAMaxAction
          IRADataLookUp={IRADataLookUp}
          actions={actions}
          customerWebViewEnabled={customerWebViewEnabled}
          hasOnlyRetirementFunds={hasOnlyRetirementFunds}
          isSalesforceRep={isSalesforceRep}
          quickBuyAgreementId={{}}
          showAllFundsStep={false}
        />
      )}
      {showRetirementHeader &&
        !isFullyContributedIRA &&
        isExchangeWithinIRALimits &&
        !bucketRoll.nextYearContributionAmountAvailable &&
        !isNonFidPlan &&
        !isAutoExchange() && <ExchangeIRAAvailable />}

      {showRetirementHeader &&
        !isFullyContributedIRA &&
        !isExchangeWithinIRALimits &&
        !isNonFidPlan &&
        isAutoExchange() && <ExchangeExcessContributionMessage />}

      {firstOfAgreement && (
        <ExpandedFundAgreementTypeHeader
          agreementType={agreementType}
          formattedEntityNames={fund.formattedEntityNames}
          productType={fund.productType}
        />
      )}
      {showRMDNote && (
        <Note
          styleName="exchange-to-fund-group-header-rmd-note"
          title={
            isCustomerViewDisabledAndIsSalesForceRep()
              ? 'Systematic RMD Warning:'
              : 'Please Note:'
          }
        >
          If you are enrolled in a Systematic RMD Program or have an existing
          automatic exchange on file it will NOT be automatically updated, and
          the distribution(s) will still occur as previously scheduled.
          {isCustomerViewDisabledAndIsSalesForceRep() ? (
            <span></span>
          ) : (
            <span>
              {' '}
              To make a change to a Systematic RMD Program or an existing
              automatic exchange,{' '}
              <a href="https://www.troweprice.com/personal-investing/help/contact-us.html">
                contact
              </a>{' '}
              a <TRP /> Retirement Specialist.
            </span>
          )}
        </Note>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    isSalesforceRep: isSalesforceRep(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    selectedFund: getSelectedExchangeFund(state),
    bucketRoll: getBucketRoll(state),
    exchangeFromIsRetail: !getExchangeFromIsIRA(state),
    hasOnlyRetirementFunds: getExchangeHasOnlyRetirementFunds(state),
    IRADataLookUp: getIRADataLookUp(state),
    isCorporateAccountType: getIsCorporateAccountType(state),
    isExchangeWithinIRALimits:
      getIsExchangeAmountWithinIRAContributionLimits(state),
    isFullyContributedIRA: getIsFullyContributedIRA(state),
    isRMDEligible: getIsRMDEligible(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(moduleActions, dispatch),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ExchangeToFundGroupHeader
);
